/**
 * Course explorer (Category → Course → Chapter → Topic → Subtopic) for the Courses module.
 *
 *   const explorer = useCourseExplorer({ api, selectedKey, onCategory });
 *   <ExplorerLayout explorer={explorer.props}> … </ExplorerLayout>
 *
 * Clicking: a category → onCategory(categoryKey | null) (list pages filter the grid);
 *           a course   → the course tab;
 *           a chapter / topic / subtopic → the course structure with that node selected.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Books, Eye, PencilSimple, TreeStructure, PlayCircle, Plus } from '@phosphor-icons/react';
import useCatalogTree from '../../hooks/useCatalogTree';
import { openLearnerPreview } from '../PreviewMode';

/**
 * Options
 *   onCategory(key|null)         list pages: filter the grid
 *   onNode(node, entry) → bool   page-level override for chapter/topic/subtopic clicks (return true when handled)
 *   nodeMenu(node, entry) → []   extra / replacement menu items for chapter/topic/subtopic rows
 *   courseMenu(node) → []        replacement menu for course rows (default: Open / Edit / Structure / Preview)
 */
export function useCourseExplorer({ api, selectedKey: controlledKey, onCategory, onNode, nodeMenu, courseMenu, counts = null, storageKey = 'courses' }) {
  const navigate = useNavigate();
  const catalog = useCatalogTree({ api, counts });
  const [localKey, setLocalKey] = useState(null);
  const selectedKey = controlledKey !== undefined ? controlledKey : localKey;

  const openStructure = (courseId, nodeId) => navigate(`/instructor/courses/${courseId}/structure${nodeId ? `?node=${nodeId}` : ''}`);

  const onSelect = (node) => {
    if (!node) { setLocalKey(null); onCategory?.(null); return; }
    if (node.kind === 'category') { setLocalKey(node.key); onCategory?.(node.data.key, node.data); return; }
    if (node.kind === 'course') { navigate(`/instructor/courses/${node.data.id}`); return; }
    if (node.kind === 'node') {
      const entry = catalog.byKey(node.key);
      if (onNode?.(node, entry)) return;
      if (entry?.course) openStructure(entry.course.data.id, node.data.id);
    }
  };

  const menuFor = (node) => {
    if (node.kind === 'course') {
      const id = node.data.id;
      return courseMenu?.(node) || [
        { key: 'open', label: 'Open course', icon: Eye, onClick: () => navigate(`/instructor/courses/${id}`) },
        { key: 'edit', label: 'Edit course', icon: PencilSimple, onClick: () => navigate(`/instructor/courses/${id}/edit`) },
        { key: 'structure', label: 'Course structure', icon: TreeStructure, onClick: () => openStructure(id) },
        { key: 'add', label: 'Add chapter…', icon: Plus, onClick: () => openStructure(id) },
        { separator: true },
        { key: 'preview', label: 'Preview as learner', icon: PlayCircle, onClick: () => openLearnerPreview(`/course/${id}`) },
      ];
    }
    if (node.kind === 'node') {
      const entry = catalog.byKey(node.key);
      const custom = nodeMenu?.(node, entry);
      if (custom) return custom;
      return entry?.course ? [{ key: 'open', label: 'Open in course structure', icon: TreeStructure, onClick: () => openStructure(entry.course.data.id, node.data.id) }] : [];
    }
    return null;
  };

  // Counts only mean anything once the tree has actually loaded; a failed load says so
  // rather than claiming the tenant has no courses.
  const courses = catalog.data?.course_count;
  const footer = catalog.error
    ? catalog.error
    : (typeof courses === 'number'
      ? `${courses} course${courses === 1 ? '' : 's'} · ${catalog.data.node_count ?? 0} chapters, topics & subtopics`
      : null);

  const props = useMemo(() => ({
    title: 'Courses', icon: Books, nodes: catalog.nodes, selectedKey, onSelect, menuFor, loading: catalog.loading, storageKey,
    emptyText: catalog.error ? 'Could not load your courses' : 'No courses yet', footer,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [catalog.nodes, catalog.loading, catalog.data, catalog.error, footer, selectedKey, storageKey, onNode, nodeMenu, courseMenu]);

  return { props, catalog, selectedKey, setSelectedKey: setLocalKey, reload: catalog.reload };
}
